import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  Bars3Icon,
  ArrowRightOnRectangleIcon,
  SunIcon,
  MoonIcon,
  ComputerDesktopIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore } from '../../stores/fileStore';
import { useThemeStore } from '../../stores/themeStore';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { authApi } from '../../api/auth';
import { useToast } from '../ui/Toaster';
import { NotificationBell } from '../notifications/NotificationBell';
import { AnalyzeProjectsButton } from '../notifications/AnalyzeProjectsButton';
import { GlobalSearch } from '../search/GlobalSearch';
import { LanguageSwitcher } from '../ui/LanguageSwitcher';
import { formatBytes } from '../../utils/dateFormat';

// Hook para debounce
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function Header() {
  const { t } = useTranslation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const toggleSidebarCollapse = useUIStore((s) => s.toggleSidebarCollapse);
  const setSearchQueryStore = useFileStore((s) => s.setSearchQuery);
  const { theme, setTheme } = useThemeStore();

  // Subscription
  const {
    planTier,
    planLimits,
    usage,
    fetchStatus: fetchSubscriptionStatus,
    isLoading: isLoadingSubscription
  } = useSubscriptionStore();

  const navigate = useNavigate();
  const toast = useToast();

  // Debounce search query
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);

  useEffect(() => {
    setSearchQueryStore(debouncedSearchQuery);
  }, [debouncedSearchQuery, setSearchQueryStore]);

  // Fetch subscription status when profile opens
  useEffect(() => {
    if (isProfileOpen && user) {
      fetchSubscriptionStatus();
    }
  }, [isProfileOpen, user, fetchSubscriptionStatus]);

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore errors, logout anyway
    }
    logout();
    toast.info(t('auth:logout.title'), t('auth:logout.message'));
    navigate('/login');
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchQueryStore(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchQueryStore('');
    inputRef.current?.focus();
  };

  const cycleTheme = () => {
    const themes: Array<'light' | 'dark' | 'system'> = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  const storagePercentage = user
    ? (user.storageUsed / user.storageLimit) * 100
    : 0;

  const ThemeIcon = theme === 'light' ? SunIcon : theme === 'dark' ? MoonIcon : ComputerDesktopIcon;

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-700 z-40 transition-colors">
      <div className="flex items-center justify-between h-full px-4">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleSidebarCollapse}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-400"
          >
            <Bars3Icon className="h-5 w-5" />
          </motion.button>

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
            </div>
            <span className="font-semibold text-gray-900 dark:text-white hidden sm:block">
              Affogato
            </span>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex-1 max-w-xl mx-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-slate-500" />
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('files:search.placeholder')}
              className="w-full pl-10 pr-10 py-2 bg-gray-100 dark:bg-slate-800 border-0 rounded-lg text-sm text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 focus:bg-white dark:focus:bg-slate-700 focus:ring-2 focus:ring-primary-500/20 focus:outline-none transition-all"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 transition-colors"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </form>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {/* Global Search */}
          <GlobalSearch />

          {/* AI Analyze Projects */}
          <AnalyzeProjectsButton />

          {/* Notifications */}
          <NotificationBell />

          {/* Theme toggle */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={cycleTheme}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-600 dark:text-slate-400"
            title={t(`theme.${theme}`)}
          >
            <ThemeIcon className="h-5 w-5" />
          </motion.button>

          {/* Profile */}
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
            >
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{user?.name}</p>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  {formatBytes(user?.storageUsed || 0)} {t('pagination.used')}
                </p>
              </div>
              <div className="w-9 h-9 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-400 font-medium">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
            </motion.button>

            {isProfileOpen && createPortal(
              <>
                <div
                  className="fixed inset-0 bg-black/50"
                  style={{ zIndex: 9998 }}
                  onClick={() => setIsProfileOpen(false)}
                />
                <div
                  className="fixed top-16 right-4 w-72 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-gray-200 dark:border-slate-700 overflow-hidden"
                  style={{ zIndex: 9999 }}
                >
                    <div className="p-4 border-b border-gray-100 dark:border-slate-700">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-lg">
                          {user?.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{user?.name}</p>
                          <p className="text-sm text-gray-500 dark:text-slate-400">{user?.email}</p>
                        </div>
                      </div>

                      {/* Storage bar */}
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mb-1">
                          <span>{t('storage.title')}</span>
                          <span>
                            {formatBytes(user?.storageUsed || 0)} / {formatBytes(user?.storageLimit || 0)}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${storagePercentage}%` }}
                            className="h-full bg-primary-500 rounded-full"
                            style={{
                              backgroundColor:
                                storagePercentage > 90
                                  ? '#ef4444'
                                  : storagePercentage > 70
                                  ? '#f97316'
                                  : undefined,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Subscription Section */}
                    <div className="p-4 border-b border-gray-100 dark:border-slate-700">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                          {t('subscription.title')}
                        </span>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                          planTier === 'pro'
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                            : 'bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300'
                        }`}>
                          {t(`subscription.${planTier}`)}
                        </span>
                      </div>

                      {isLoadingSubscription ? (
                        <div className="flex justify-center py-2">
                          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                      ) : planLimits && usage ? (
                        <div className="space-y-3">
                          {/* Clients usage */}
                          <div>
                            <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mb-1">
                              <span>{t('subscription.clients')}</span>
                              <span>
                                {planLimits.clients === -1 ? (
                                  t('subscription.unlimited')
                                ) : (
                                  `${usage.clientsCreated} ${t('subscription.of')} ${planLimits.clients}`
                                )}
                              </span>
                            </div>
                            {planLimits.clients !== -1 && (
                              <div className="h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min(100, (usage.clientsCreated / planLimits.clients) * 100)}%` }}
                                  className="h-full bg-blue-500 rounded-full"
                                />
                              </div>
                            )}
                          </div>

                          {/* Diagnostics usage */}
                          <div>
                            <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mb-1">
                              <span>{t('subscription.diagnostics')}</span>
                              <span>
                                {planLimits.diagnostics === -1 ? (
                                  t('subscription.unlimited')
                                ) : (
                                  `${usage.diagnosticsCreated} ${t('subscription.of')} ${planLimits.diagnostics}`
                                )}
                              </span>
                            </div>
                            {planLimits.diagnostics !== -1 && (
                              <div className="h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min(100, (usage.diagnosticsCreated / planLimits.diagnostics) * 100)}%` }}
                                  className="h-full bg-green-500 rounded-full"
                                />
                              </div>
                            )}
                          </div>

                          {/* AI uses */}
                          <div>
                            <div className="flex justify-between text-xs text-gray-500 dark:text-slate-400 mb-1">
                              <span>{t('subscription.aiUses')}</span>
                              <span>
                                {planLimits.aiUsesPerMonth === -1 ? (
                                  t('subscription.unlimited')
                                ) : (
                                  `${usage.aiUses} ${t('subscription.of')} ${planLimits.aiUsesPerMonth}`
                                )}
                              </span>
                            </div>
                            {planLimits.aiUsesPerMonth !== -1 && (
                              <div className="h-1.5 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <motion.div
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.min(100, (usage.aiUses / planLimits.aiUsesPerMonth) * 100)}%` }}
                                  className="h-full bg-purple-500 rounded-full"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    {/* Language Switcher */}
                    <div className="border-b border-gray-100 dark:border-slate-700">
                      <LanguageSwitcher />
                    </div>

                    <div className="p-2">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5" />
                        {t('buttons.signOut')}
                      </button>
                    </div>
                  </div>
                </>,
              document.body
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
